import OauthButtons, {
  collectProviderSession,
  oauthErrorMessage,
} from "@app/components/oauth-buttons";
import { setAuthToken } from "@app/relay/fetch-relay";
import { environment } from "@app/relay/relay-environment";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { commitMutation, graphql } from "react-relay";

import type { login_CreateSessionMutation } from "@app/queries/login_CreateSessionMutation.graphql";
import { pageHead } from "@app/lib/page-title";

const createSessionMutation = graphql`
  mutation login_CreateSessionMutation(
    $createSessionInput: CreateSessionInputObject!
  ) {
    createSession(createSessionInput: $createSessionInput) {
      user {
        id
      }
      token
      errors {
        message
      }
    }
  }
`;

type LoginSearch = { redirect?: string; oauth?: string; oauth_error?: string };

const stringParam = (value: unknown) =>
  typeof value === "string" ? value : undefined;

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: stringParam(search.redirect),
    oauth: stringParam(search.oauth),
    oauth_error: stringParam(search.oauth_error),
  }),
  head: () => pageHead("Sign in"),
  component: LoginPage,
});

type FormValues = { email: string; password: string };

function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });
  const { register, handleSubmit } = useForm<FormValues>();
  const [error, setError] = useState<string | null>(() =>
    search.oauth_error ? oauthErrorMessage(search.oauth_error) : null
  );
  const providerDone = search.oauth === "done";
  const [submitting, setSubmitting] = useState(providerDone);

  // Back from Google or Microsoft: the session is waiting in this browser's
  // cookie. Once, though React mounts this twice in development — the server
  // hands it over a single time, and a second ask would only find it gone.
  const collected = useRef(false);
  useEffect(() => {
    if (!providerDone || collected.current) return;
    collected.current = true;

    void collectProviderSession()
      .catch(() => null)
      .then((jwt) => {
        if (jwt) {
          setAuthToken(jwt);
          window.location.replace("/");
          return;
        }
        setSubmitting(false);
        setError(oauthErrorMessage("FAILED"));
      });
  }, [providerDone]);

  const onSubmit = (values: FormValues) => {
    setSubmitting(true);
    setError(null);

    commitMutation<login_CreateSessionMutation>(environment, {
      mutation: createSessionMutation,
      variables: { createSessionInput: values },
      onCompleted: (response) => {
        setSubmitting(false);
        const payload = response.createSession;

        if (payload?.token) {
          setAuthToken(payload.token);
          window.location.assign(search.redirect ?? "/");
        } else {
          setError(
            payload?.errors?.[0]?.message ?? "Invalid email or password"
          );
        }
      },
      onError: (err) => {
        setSubmitting(false);
        setError(err.message);
      },
    });
    void navigate; // navigation happens via location.assign to reset relay store
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white shadow rounded-lg p-8 w-full max-w-sm">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <h1 className="text-2xl font-bold text-center">QBO Sync</h1>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Email</span>
            <input
              type="email"
              autoComplete="email"
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
              {...register("email", { required: true })}
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
              {...register("password", { required: true })}
            />
          </label>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded bg-gray-900 text-white py-2 font-medium disabled:opacity-50"
          >
            {submitting ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <span aria-hidden className="h-px flex-1 bg-zinc-200" />
          <span className="text-caption uppercase tracking-[0.12em] text-zinc-400">
            or
          </span>
          <span aria-hidden className="h-px flex-1 bg-zinc-200" />
        </div>

        <OauthButtons intent="login" disabled={submitting} />
      </div>
    </div>
  );
}
